/**
 * A MARKDOWN DOCUMENT, READ WHERE IT IS NEEDED.
 *
 * Resources mints a signed URL and opens it in a new tab, which is right for a
 * PDF and wrong for a `.md`: the browser downloads it, or shows a wall of `#`
 * and `|`. The Staff Journey Brief is a desk aid — somebody is reading it with
 * a customer on the line — and raw markdown is unusable at exactly that moment.
 *
 * So a markdown document opens HERE, in the card, and the tab-opening path
 * stays for everything else and for anybody who still wants the file.
 *
 * NOTHING HERE BUILDS MARKUP FROM A STRING. `parseMarkdown` returns plain
 * objects and this walks them into React children, which React escapes. These
 * documents are uploaded by staff, so their text is input; a renderer that set
 * HTML from it would turn the one shelf every employee can read into an
 * injection surface. See the header of lib/markdown.js.
 *
 * ---------------------------------------------------------------------------
 * THE WATERMARK, AND AN HONEST ACCOUNT OF WHAT IT DOES
 *
 * The founder: *"No company document openable in the Portal should be able to
 * be downloaded or have a screenshot taken of it."*
 *
 * **A screenshot cannot be prevented on the web.** There is no browser API for
 * it — not in CSS, not in JavaScript — and the operating system's own capture
 * tools sit entirely outside this page. Blur-on-blur, right-click traps and
 * `user-select: none` are defeated in seconds by anybody who wants to, and they
 * break a staff member legitimately copying one sentence to paste to a
 * customer. Building them would leave somebody believing the reputation weights
 * were protected when they were not, which is worse than knowing they are not.
 *
 * So this does the thing that does work: **it makes a capture attributable.**
 * The reader's own name and address are tiled across the document they are
 * reading, at low contrast and behind the text. A screenshot of this page
 * carries the name of the person who took it, which is what every serious
 * document-control system relies on for exactly this reason — deterrence and
 * attribution, not prevention.
 *
 * It is `aria-hidden` and `pointer-events: none`: it is for the camera, and a
 * screen reader reading a name twelve times between paragraphs would make the
 * document unusable for the person who most needs it read aloud.
 */

import { useEffect, useState } from 'react';
import { O, SANS, Notice, Spinner } from '../ui';
import { parseMarkdown } from '../lib/markdown';

const Spans = ({ spans }) => (
  <>
    {spans.map((s, i) => {
      if (s.kind === 'strong') return <strong key={i} style={{ fontWeight: 700, color: O.ink }}>{s.text}</strong>;
      if (s.kind === 'em') return <em key={i}>{s.text}</em>;
      if (s.kind === 'code') {
        return (
          <code key={i} style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.92em', background: O.canvas, padding: '1px 5px',
            borderRadius: 4, border: `1px solid ${O.hairline}`,
          }}>{s.text}</code>
        );
      }
      return <span key={i}>{s.text}</span>;
    })}
  </>
);

const HEADING_SIZE = { 1: 19, 2: 16, 3: 14, 4: 13, 5: 12.5, 6: 12.5 };

const Block = ({ block }) => {
  switch (block.type) {
    case 'h': {
      // The document's own h1 is its title, which the card already shows above
      // this, so it is set no larger than an h2 -- two titles of the same
      // weight, one under the other, reads as a mistake.
      const size = HEADING_SIZE[block.level] || 12.5;
      return (
        <p style={{
          fontFamily: SANS, fontSize: size, fontWeight: 700, color: O.ink,
          margin: block.level <= 2 ? '22px 0 8px' : '16px 0 6px',
          lineHeight: 1.3,
        }}>
          <Spans spans={block.spans} />
        </p>
      );
    }
    case 'p':
      return (
        <p style={{
          fontFamily: SANS, fontSize: 12.5, lineHeight: 1.75, color: O.inkMuted,
          margin: '0 0 10px', maxWidth: '74ch',
        }}>
          <Spans spans={block.spans} />
        </p>
      );
    case 'quote':
      return (
        <div style={{
          borderLeft: `3px solid ${O.hairlineOn}`, paddingLeft: 12,
          margin: '0 0 12px',
        }}>
          <p style={{
            fontFamily: SANS, fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted,
            margin: 0, maxWidth: '72ch',
          }}>
            <Spans spans={block.spans} />
          </p>
        </div>
      );
    case 'ul':
    case 'ol': {
      const List = block.type === 'ol' ? 'ol' : 'ul';
      return (
        <List style={{
          fontFamily: SANS, fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted,
          margin: '0 0 12px', paddingLeft: 20, maxWidth: '72ch',
        }}>
          {block.items.map((item, i) => (
            <li key={i} style={{ marginBottom: 4 }}><Spans spans={item} /></li>
          ))}
        </List>
      );
    }
    case 'table':
      // THE ONE THING ALLOWED TO BE WIDER THAN THE COLUMN. A staff brief is
      // mostly tables; wrapping a five-column table into the reading measure
      // makes it unreadable, so it scrolls sideways in its own box instead.
      return (
        <div style={{ overflowX: 'auto', margin: '0 0 14px' }}>
          <table style={{ borderCollapse: 'collapse', fontFamily: SANS, fontSize: 12 }}>
            <thead>
              <tr>
                {block.head.map((cell, i) => (
                  <th key={i} style={{
                    textAlign: 'left', padding: '7px 12px 7px 0', color: O.ink,
                    fontWeight: 700, borderBottom: `1px solid ${O.hairlineOn}`,
                    whiteSpace: 'nowrap',
                  }}><Spans spans={cell} /></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} style={{
                      padding: '7px 12px 7px 0', color: O.inkMuted,
                      borderBottom: `1px solid ${O.hairline}`, verticalAlign: 'top',
                      lineHeight: 1.6,
                    }}><Spans spans={cell} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'pre':
      return (
        <pre style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 11.5, lineHeight: 1.6, color: O.inkMuted, background: O.canvas,
          border: `1px solid ${O.hairline}`, borderRadius: 8, padding: 12,
          overflowX: 'auto', margin: '0 0 12px',
        }}>{block.text}</pre>
      );
    case 'hr':
      return <hr style={{ border: 0, borderTop: `1px solid ${O.hairline}`, margin: '18px 0' }} />;
    default:
      return null;
  }
};

/**
 * Tiled, low-contrast, behind the text. Repeated rather than placed once
 * because a single mark is cropped out of a screenshot without effort.
 */
const Watermark = ({ who }) => {
  if (!who) return null;
  return (
    <div
      aria-hidden="true"
      data-watermark
      style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden',
        display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start',
        gap: '64px 48px', padding: 24, zIndex: 0,
      }}
    >
      {Array.from({ length: 40 }).map((_, i) => (
        <span
          key={i}
          style={{
            fontFamily: SANS, fontSize: 11, color: O.inkFaint, opacity: 0.16,
            transform: 'rotate(-24deg)', whiteSpace: 'nowrap', userSelect: 'none',
          }}
        >
          {who}
        </span>
      ))}
    </div>
  );
};

export default function MarkdownDoc({ url, staff = null }) {
  const [state, setState] = useState({ loading: true, error: null, text: '' });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // no-store: the bytes are not left sitting in the browser's disk cache
        // after the signed URL that fetched them has expired.
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error(`the file came back ${res.status}`);
        const text = await res.text();
        if (alive) setState({ loading: false, error: null, text });
      } catch (e) {
        // A signed URL expires, and the honest message names that rather than
        // blaming the document: the row is fine, the link went stale.
        if (alive) {
          setState({
            loading: false,
            error: `That document could not be read (${e.message}). Signed links expire — close this and open it again.`,
            text: '',
          });
        }
      }
    })();
    return () => { alive = false; };
  }, [url]);

  if (state.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0' }}>
        <Spinner size={16} />
        <span style={{ fontFamily: SANS, fontSize: 12.5, color: O.inkMuted }}>
          Opening the document…
        </span>
      </div>
    );
  }
  if (state.error) return <Notice tone="critical">{state.error}</Notice>;

  const blocks = parseMarkdown(state.text);
  // Name, address and the moment. The timestamp is what turns "somebody leaked
  // this" into "this copy was on screen at 02:14 on the 16th".
  const who = staff
    ? [staff.full_name, staff.email].filter(Boolean).join(' · ')
      + ` · ${new Date().toLocaleString('en-ZA')}`
    : null;

  return (
    <div data-markdown-doc style={{ position: 'relative', paddingTop: 4 }}>
      <Watermark who={who} />
      {/* Above the mark, so the document stays readable. */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {blocks.map((b, i) => <Block key={i} block={b} />)}
      </div>
    </div>
  );
}
